// The name of a row in an accordion or outline (a Service group / vessel / work, a GRP boat / item), shared
// by the Service and GRP pages.
//   mouse — today's always-live input: click into it to rename, the change saves on blur.
//   touch — wrapping text, so a tap anywhere on a header opens the card instead of raising the keyboard
//           (and can't commit a stray rename). Renaming moves to an explicit control: pass `rename` for a
//           ✎ button that swaps the text for the input in place, or offer the name as a field elsewhere
//           (Service puts it first in the vessel and work fields panels).
//   right-click — renames too: with a mouse it selects the whole name in its field, ready to type over;
//           on touch (a long-press, where the browser reports one) it opens the field like the ✎ does.
//   cut short — a name too long for its box ends in "…" with a mouse; pass `fullTip` and, while the pointer
//           is on it, the whole name shows in a hover card (or, with hover cards off in Settings, the
//           browser's own tooltip). On touch the text wraps, so it's whole already.
import { useState, type SVGProps } from 'react';
import type { TipHandlers } from './HoverTip.js';
import { useIsCoarse } from '../lib/media.js';

/** pencil — rename (Feather style, like components/icons.tsx) */
function PencilIcon({ size = 16, ...rest }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: 'middle', flexShrink: 0 }} {...rest}>
      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

const stop = (e: { stopPropagation(): void }) => e.stopPropagation();

export function NameField({
  value,
  onCommit,
  canWrite,
  className,
  textClassName,
  placeholder,
  ariaLabel,
  readOnly = 'input',
  rename = false,
  fullTip,
}: {
  /** the saved name */
  value: string;
  /** called on blur when the text changed (an empty string when cleared) */
  onCommit: (next: string) => void;
  canWrite: boolean;
  /** the live input's class (mouse), e.g. 'svc2-vname-in' */
  className: string;
  /** the wrapping text's class (touch), e.g. 'svc2-vname' */
  textClassName: string;
  placeholder: string;
  ariaLabel?: string;
  /** what a viewer without write access sees with a mouse: a disabled input (Service) or plain text (GRP) */
  readOnly?: 'input' | 'text';
  /** touch: a ✎ button beside the text that turns it into the input in place */
  rename?: boolean;
  /** mouse: the hover card for the whole name when it's cut short (the page's `useHoverTip().bind`, given
   *  the text) */
  fullTip?: (text: string) => TipHandlers;
}) {
  const coarse = useIsCoarse();
  const [editing, setEditing] = useState(false);
  // with a mouse: whether the name was cut short when the pointer last came onto it
  const [cut, setCut] = useState(false);

  if (!coarse) {
    if (!canWrite && readOnly === 'text') return <span className={className}>{value || placeholder}</span>;
    const tip = fullTip && value ? fullTip(value) : null;
    // read-only, the field is disabled (and not every browser tells the page the pointer is on it): the
    // whole name is its tooltip
    const hint = canWrite ? 'Click or right-click to rename' : fullTip ? value || undefined : undefined;
    return (
      <input
        key={value}
        className={className}
        defaultValue={value}
        disabled={!canWrite}
        placeholder={placeholder}
        aria-label={ariaLabel}
        // cut short: the whole name, in the hover card when there is one (not twice), else as the tooltip
        title={tip && cut ? (tip.onMouseEnter ? undefined : value) : hint}
        onMouseEnter={tip ? (e) => {
          const el = e.currentTarget;
          // while it's being renamed the box shows the text itself
          const c = document.activeElement !== el && el.scrollWidth > el.clientWidth + 1;
          setCut(c);
          if (c) tip.onMouseEnter?.(e);
        } : undefined}
        onMouseLeave={tip ? () => tip.onMouseLeave?.() : undefined}
        onClick={stop}
        onMouseDown={canWrite || tip ? (e) => {
          // pressed into, to rename: the card goes
          tip?.onMouseLeave?.();
          // a right-button press on a name not being edited mustn't drop the caret where it landed
          if (canWrite && e.button === 2 && document.activeElement !== e.currentTarget) e.preventDefault();
        } : undefined}
        onContextMenu={canWrite ? (e) => {
          // the first right-click picks the name; a second one, once it's being edited, is the usual menu
          if (document.activeElement === e.currentTarget) return;
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.focus();
          e.currentTarget.select();
        } : undefined}
        onBlur={(e) => { if (e.target.value !== value) onCommit(e.target.value); }}
      />
    );
  }

  if (editing && canWrite) {
    return (
      <input
        className={`${className} is-editing`}
        defaultValue={value}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        autoFocus
        enterKeyHint="done"
        onClick={stop}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === 'Escape') { e.preventDefault(); e.currentTarget.value = value; e.currentTarget.blur(); }
        }}
        onBlur={(e) => { if (e.target.value !== value) onCommit(e.target.value); setEditing(false); }}
      />
    );
  }

  return (
    <>
      <span
        className={textClassName}
        onContextMenu={rename && canWrite ? (e) => { e.preventDefault(); e.stopPropagation(); setEditing(true); } : undefined}
      >{value || <span className="name-empty">{placeholder}</span>}</span>
      {rename && canWrite && (
        <button
          type="button"
          className="name-edit hit-44"
          aria-label={value ? `Rename ${value}` : (ariaLabel ? `Set ${ariaLabel.toLowerCase()}` : 'Rename')}
          title="Rename"
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        >
          <PencilIcon size={15} />
        </button>
      )}
    </>
  );
}
